import { ToggleGroup } from "radix-ui";
import type { ReactNode } from "react";

import { cx } from "@/lib/cx";

import styles from "./SegmentedControl.module.css";

export interface Segment<V extends string> {
  value: V;
  label: string;
  icon?: ReactNode;
}

export interface SegmentedControlProps<V extends string> {
  /** Accessible name of the group. */
  label: string;
  value: V;
  onValueChange: (value: V) => void;
  segments: readonly Segment<V>[];
  /** Show icons only; labels stay available to assistive technology. */
  iconOnly?: boolean;
  className?: string | undefined;
}

/** One choice from a few, always visible (layout switchers, units). Arrow keys move between segments. */
export function SegmentedControl<V extends string>({
  label,
  value,
  onValueChange,
  segments,
  iconOnly = false,
  className,
}: SegmentedControlProps<V>) {
  return (
    <ToggleGroup.Root
      type="single"
      aria-label={label}
      value={value}
      onValueChange={(next) => {
        const match = segments.find((segment) => segment.value === next);
        if (match !== undefined) onValueChange(match.value);
      }}
      className={cx(styles.group, className)}
    >
      {segments.map((segment) => (
        <ToggleGroup.Item
          key={segment.value}
          value={segment.value}
          aria-label={iconOnly ? segment.label : undefined}
          title={iconOnly ? segment.label : undefined}
          className={styles.segment}
        >
          {segment.icon !== undefined && <span aria-hidden="true">{segment.icon}</span>}
          {!iconOnly && segment.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
